import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { osFetch } from "../api";
import { useOS } from "../OSContext";
import { toast } from "sonner";
import { SnoozeMenu } from "../shared/panels";
import {
  Radar, Loader2, RefreshCw, ScanLine, ChevronDown, ChevronRight,
  CheckCircle2, Clock, XCircle, AlertTriangle, ShieldAlert, Sparkles, RotateCcw,
} from "lucide-react";

// Category → visual treatment. Signal, not noise: color communicates urgency at a glance.
export const CAT = {
  Critical: "text-rose-300 border-rose-500/40 bg-rose-500/10",
  High: "text-orange-300 border-orange-500/40 bg-orange-500/10",
  Medium: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  Low: "text-slate-300 border-white/15 bg-white/5",
  Opportunity: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  Recommendation: "text-cyan-300 border-cyan-500/40 bg-cyan-500/10",
  Warning: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  Risk: "text-rose-300 border-rose-500/40 bg-rose-500/10",
  Blocked: "text-rose-300 border-rose-500/40 bg-rose-500/10",
  "Waiting on Human": "text-violet-300 border-violet-500/40 bg-violet-500/10",
  "Waiting on AI": "text-blue-300 border-blue-500/40 bg-blue-500/10",
  "Needs Review": "text-slate-300 border-white/15 bg-white/5",
};

const SCAN_ICON = { complete: CheckCircle2, running: Loader2, pending: Clock, failed: XCircle };
const SCAN_COLOR = { complete: "text-emerald-400", running: "text-cyan-400", pending: "text-amber-400", failed: "text-rose-400" };

function Chip({ label, value }) {
  return (
    <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 border border-white/10 rounded-full px-2.5 py-1">
      {label} <span className="text-white ml-1">{value}</span>
    </span>
  );
}

function ItemRow({ item, onAction, busy }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/30 p-3.5" data-testid={`os-attention-item-${item.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] font-mono uppercase tracking-widest border rounded-full px-2 py-0.5 ${CAT[item.category] || CAT["Needs Review"]}`}>{item.category}</span>
        <span className="text-[10px] font-mono text-slate-500">sev {item.severity} · conf {item.confidence}%</span>
        <Link to={`/os/catalog/${item.entity_id}`} className="text-[11px] text-slate-500 hover:text-[#00F0FF] ml-auto truncate max-w-[40%]">{item.entity_type}: {item.entity_title}</Link>
      </div>
      <p className="text-sm text-slate-300 mt-2">{item.reason}</p>
      {item.recommended_action && <p className="text-xs text-slate-400 mt-1.5"><span className="text-[#00F0FF] font-mono text-[10px] uppercase mr-1">Do</span>{item.recommended_action}</p>}
      {item.estimated_impact && <p className="text-xs text-slate-500 mt-1"><span className="text-slate-600 font-mono text-[10px] uppercase mr-1">Impact</span>{item.estimated_impact}</p>}
      <div className="flex items-center gap-2 mt-3">
        <button onClick={() => onAction(item.id, "resolve")} disabled={!!busy} className="text-[11px] font-medium text-emerald-300 hover:text-emerald-200 flex items-center gap-1 disabled:opacity-40" data-testid={`os-attention-resolve-${item.id}`}><CheckCircle2 size={13} /> Resolve</button>
        <SnoozeMenu onSnooze={(body) => onAction(item.id, "snooze", body)} />
        <button onClick={() => onAction(item.id, "dismiss")} disabled={!!busy} className="text-[11px] font-medium text-slate-400 hover:text-slate-200 flex items-center gap-1 disabled:opacity-40" data-testid={`os-attention-dismiss-${item.id}`}><XCircle size={13} /> Dismiss</button>
        {busy === item.id && <Loader2 size={13} className="animate-spin text-slate-500" />}
      </div>
    </div>
  );
}

function GroupCard({ group, onAction, busy }) {
  const [open, setOpen] = useState(false);
  const p = group.priority_score;
  const pColor = p >= 70 ? "bg-rose-500/15 text-rose-300 border-rose-500/40" : p >= 40 ? "bg-amber-500/15 text-amber-300 border-amber-500/40" : "bg-white/5 text-slate-300 border-white/15";
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5" data-testid={`os-attention-group-${group.id}`}>
      <div className="flex items-start gap-3">
        <div className={`shrink-0 w-12 h-12 rounded-xl border flex flex-col items-center justify-center ${pColor}`} title="Priority score">
          <span className="font-display font-extrabold leading-none">{p}</span>
          <span className="text-[8px] font-mono uppercase tracking-widest opacity-70">prio</span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-display font-bold text-[15px] leading-snug">{group.headline}</p>
          <p className="text-slate-500 text-sm mt-1"><span className="text-slate-600 font-mono text-[10px] uppercase mr-1">Root cause</span>{group.root_cause}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <Chip label="Impact" value={group.business_impact} />
        <Chip label="Urgency" value={group.urgency} />
        <Chip label="Confidence" value={`${group.confidence}%`} />
        <Chip label="Signals" value={group.open_count ?? group.item_count} />
      </div>

      {group.recommended_action && (
        <div className="mt-4 rounded-xl border border-[#0055FF]/30 bg-[#0055FF]/10 p-3.5">
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] mb-1">Recommended next action</p>
          <p className="text-sm text-white">{group.recommended_action}</p>
        </div>
      )}

      {group.ai_reasoning && <p className="text-xs text-slate-400 mt-3 flex gap-2"><Sparkles size={13} className="text-[#00F0FF] shrink-0 mt-0.5" />{group.ai_reasoning}</p>}
      {group.supporting_evidence?.length > 0 && (
        <ul className="mt-2 space-y-1">
          {group.supporting_evidence.map((e, i) => <li key={i} className="text-xs text-slate-500 flex gap-2"><span className="text-slate-700">•</span>{e}</li>)}
        </ul>
      )}
      {group.related_entities?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {group.related_entities.map((r) => (
            <Link key={r.id} to={`/os/catalog/${r.id}`} className="text-[10px] font-mono text-slate-400 border border-white/10 rounded-full px-2 py-0.5 hover:text-[#00F0FF] hover:border-[#00F0FF]/40 transition-colors">{r.title || r.id}</Link>
          ))}
        </div>
      )}

      <button onClick={() => setOpen((o) => !o)} className="mt-4 text-[11px] font-mono uppercase tracking-widest text-slate-500 hover:text-white flex items-center gap-1" data-testid={`os-attention-group-drill-${group.id}`}>
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {open ? "Hide" : "Drill into"} {group.items?.length || 0} underlying {group.items?.length === 1 ? "issue" : "issues"}
      </button>
      {open && (
        <div className="mt-3 space-y-2.5">
          {group.items?.map((it) => <ItemRow key={it.id} item={it} onAction={onAction} busy={busy} />)}
        </div>
      )}
    </div>
  );
}

export default function AttentionCenter() {
  const { can, orgId, reloadAttention } = useOS();
  const [data, setData] = useState(null);
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [filter, setFilter] = useState(null);
  const [counts, setCounts] = useState({ by_category: {}, total_open: 0 });
  const [snoozed, setSnoozed] = useState([]);

  const load = useCallback(async () => {
    try {
      const [g, s, c, sn] = await Promise.all([
        osFetch("/attention/groups"),
        osFetch("/attention/scan/latest"),
        osFetch("/attention/counts"),
        osFetch("/attention?status=snoozed"),
      ]);
      setData(g); setScan(s); setCounts(c); setSnoozed(sn);
    } catch (e) { setData({ groups: [], ungrouped: [] }); toast.error(e.message); }
  }, []);

  useEffect(() => { load(); }, [orgId, load]);

  const onAction = async (id, kind, body) => {
    setBusy(id);
    try {
      await osFetch(`/attention/${id}/${kind}`, { method: "POST", body: kind === "snooze" ? (body || { hours: 24 }) : undefined });
      toast.success(kind === "resolve" ? "Marked resolved" : kind === "snooze" ? "Snoozed" : kind === "resume" ? "Reactivated" : "Dismissed");
      await load(); reloadAttention?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const scanAll = async () => {
    setScanning(true);
    toast.info("ArcaOS is scanning the company…");
    try {
      const r = await osFetch("/attention/scan/all", { method: "POST" });
      toast.success(`Scan complete — ${r.items_generated} signals, ${r.groups_generated} groups`);
      await load(); reloadAttention?.();
    } catch (e) { toast.error(e.message); } finally { setScanning(false); }
  };

  const groups = (data?.groups || []).filter((g) => !filter || (g.items || []).some((i) => i.category === filter));
  const ungrouped = (data?.ungrouped || []).filter((i) => !filter || i.category === filter);
  const empty = data && groups.length === 0 && ungrouped.length === 0;
  const ScanIcon = scan ? (SCAN_ICON[scan.status] || Clock) : null;

  return (
    <div className="max-w-5xl" data-testid="os-attention">
      <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-[#00F0FF] mb-1 flex items-center gap-2"><Radar size={13} /> AI Attention Center</p>
          <h1 className="font-display font-extrabold text-3xl">What deserves your attention</h1>
          <p className="text-slate-500 mt-1 max-w-xl text-sm">Your executive chief of staff. Related issues are grouped by root cause and prioritized by business impact — signal, not noise.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="text-slate-400 hover:text-white border border-white/10 rounded-xl p-2.5 transition-colors" title="Refresh" data-testid="os-attention-refresh"><RefreshCw size={15} /></button>
          {can("manage_members") && (
            <button onClick={scanAll} disabled={scanning} className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-4 py-2.5 rounded-xl font-semibold hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-50" data-testid="os-attention-scan-all">
              {scanning ? <Loader2 size={15} className="animate-spin" /> : <ScanLine size={15} />} {scanning ? "Scanning…" : "Scan all"}
            </button>
          )}
        </div>
      </div>

      {/* Scan status strip */}
      {scan && (
        <div className="flex items-center gap-3 mt-4 text-[11px] font-mono text-slate-500 flex-wrap" data-testid="os-attention-scan-status">
          {ScanIcon && <span className={`flex items-center gap-1.5 ${SCAN_COLOR[scan.status] || ""}`}><ScanIcon size={13} className={scan.status === "running" ? "animate-spin" : ""} /> {scan.status}</span>}
          <span>· last scan {new Date(scan.completed_at || scan.started_at).toLocaleString()}</span>
          <span>· model {scan.model}</span>
          <span>· prompt {scan.prompt_version}</span>
        </div>
      )}

      {/* Category filter */}
      {Object.keys(counts.by_category || {}).length > 0 && (
        <div className="flex flex-wrap gap-2 mt-5">
          <button onClick={() => setFilter(null)} className={`text-[10px] font-mono uppercase tracking-widest border rounded-full px-3 py-1 ${!filter ? "text-white border-white/30 bg-white/5" : "text-slate-500 border-white/10"}`} data-testid="os-attention-filter-all">All ({counts.total_open})</button>
          {Object.entries(counts.by_category).map(([c, n]) => (
            <button key={c} onClick={() => setFilter(c === filter ? null : c)} className={`text-[10px] font-mono uppercase tracking-widest border rounded-full px-3 py-1 ${filter === c ? CAT[c] : "text-slate-500 border-white/10 hover:text-white"}`} data-testid={`os-attention-filter-${c}`}>{c} ({n})</button>
          ))}
        </div>
      )}

      {/* Body */}
      <div className="mt-6 space-y-4">
        {data === null && <div className="flex items-center gap-2 text-slate-400 py-16"><Loader2 className="animate-spin" /> Loading intelligence…</div>}
        {empty && (
          <div className="rounded-2xl border border-dashed border-white/15 p-12 text-center" data-testid="os-attention-empty">
            <ShieldAlert size={28} className="text-emerald-400 mx-auto mb-3" />
            <p className="text-slate-300 font-display font-bold">All clear</p>
            <p className="text-slate-600 text-sm mt-1">Nothing needs your attention right now. ArcaOS keeps watching as the company builds.</p>
          </div>
        )}
        {groups.map((g) => <GroupCard key={g.id} group={g} onAction={onAction} busy={busy} />)}
        {ungrouped.length > 0 && (
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-600 mb-2 mt-6 flex items-center gap-2"><AlertTriangle size={12} /> Unclustered signals</p>
            <div className="space-y-2.5">{ungrouped.map((it) => <ItemRow key={it.id} item={it} onAction={onAction} busy={busy} />)}</div>
          </div>
        )}
        {snoozed.length > 0 && (
          <div data-testid="os-attention-snoozed">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-600 mb-2 mt-6 flex items-center gap-2"><Clock size={12} /> Snoozed ({snoozed.length})</p>
            <div className="space-y-2.5">
              {snoozed.map((it) => (
                <div key={it.id} className="rounded-xl border border-white/10 bg-black/20 p-3.5 opacity-80" data-testid={`os-attention-snoozed-${it.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-mono uppercase tracking-widest border rounded-full px-2 py-0.5 ${CAT[it.category] || CAT["Needs Review"]}`}>{it.category}</span>
                    <span className="text-[10px] font-mono text-violet-300">
                      {it.snooze_condition?.type === "until_manual" ? "until manually resumed"
                        : it.snooze_condition?.type && it.snooze_condition.type !== "until_datetime" ? `${it.snooze_condition.type.replace(/_/g, " ")}${it.snooze_condition.ref_title ? `: ${it.snooze_condition.ref_title}` : ""}`
                        : it.snooze_until ? `until ${new Date(it.snooze_until).toLocaleString()}` : "snoozed"}
                    </span>
                    <button onClick={() => onAction(it.id, "resume")} disabled={busy === it.id} className="ml-auto text-[11px] font-medium text-cyan-300 hover:text-cyan-200 flex items-center gap-1" data-testid={`os-attention-resume-${it.id}`}><RotateCcw size={12} /> Resume</button>
                  </div>
                  <p className="text-sm text-slate-400 mt-1.5">{it.reason}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Compact panel for the Executive Dashboard.
export function AttentionCenterPanel() {
  const { orgId } = useOS();
  const [groups, setGroups] = useState(null);
  useEffect(() => { osFetch("/attention/groups").then((d) => setGroups(d.groups || [])).catch(() => setGroups([])); }, [orgId]);
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5" data-testid="os-dashboard-attention">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Radar size={16} className="text-[#00F0FF]" /><h2 className="font-display font-bold text-lg">AI Attention Center</h2></div>
        <Link to="/os/attention" className="text-[11px] font-mono uppercase tracking-widest text-[#00F0FF] hover:underline" data-testid="os-dashboard-attention-link">Open →</Link>
      </div>
      {groups === null ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm py-6"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : groups.length === 0 ? (
        <p className="text-slate-600 text-sm py-4">All clear — nothing needs your attention right now.</p>
      ) : (
        <ul className="space-y-2.5">
          {groups.slice(0, 4).map((g) => (
            <li key={g.id}>
              <Link to="/os/attention" className="flex items-start gap-3 group">
                <span className={`shrink-0 w-8 h-8 rounded-lg border flex items-center justify-center text-xs font-display font-bold ${g.priority_score >= 70 ? "text-rose-300 border-rose-500/40 bg-rose-500/10" : g.priority_score >= 40 ? "text-amber-300 border-amber-500/40 bg-amber-500/10" : "text-slate-300 border-white/15"}`}>{g.priority_score}</span>
                <span className="text-sm text-slate-300 group-hover:text-white transition-colors leading-snug">{g.headline}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
